/**
 * Complaints page — Phase 1 of the Amaravati TVS Complaint Redressal System.
 * Handles register / assign / classify / status transitions with SLA display.
 * Customer WhatsApp acknowledgement is fired automatically on register by the
 * backend (best-effort — status shown as "ack sent" or "ack failed").
 */
import { useCallback, useEffect, useMemo, useState, Fragment } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { WarningCircle, Plus, X, CheckCircle, Clock, ArrowUp } from "@phosphor-icons/react";
import PageHeader from "@/components/PageHeader";
import BranchFilter from "@/components/BranchFilter";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/format";

const CATEGORIES = [
  { v: "sales", l: "Sales" }, { v: "service", l: "Service" }, { v: "parts", l: "Parts" },
  { v: "billing", l: "Billing" }, { v: "customer_care", l: "Customer Care" }, { v: "other", l: "Other" },
];
const SEVERITIES = [
  { v: "critical", l: "Critical", css: "border-destructive bg-destructive/10 text-destructive" },
  { v: "high",     l: "High",     css: "border-[#E08600] bg-[#FFF6E5] text-[#E08600]" },
  { v: "medium",   l: "Medium",   css: "border-canvas-line bg-white text-ink" },
  { v: "low",      l: "Low",      css: "border-canvas-line bg-canvas-panel text-ink-muted" },
];
const SOURCES = [
  { v: "showroom", l: "Showroom" }, { v: "workshop", l: "Workshop" },
  { v: "phone", l: "Phone" }, { v: "whatsapp", l: "WhatsApp" },
  { v: "website", l: "Website" }, { v: "customer_care", l: "Customer Care" },
];
const STATUSES = ["open", "in_progress", "resolved", "closed", "escalated"];

function StatusPill({ status }) {
  const map = {
    open: "border-destructive text-destructive bg-destructive/5",
    in_progress: "border-[#0057b7] text-[#0057b7] bg-[#0057b7]/5",
    resolved: "border-[#00B341] text-[#00B341] bg-[#00B341]/5",
    closed: "border-canvas-line text-ink-muted bg-canvas-panel",
    escalated: "border-[#E08600] text-[#E08600] bg-[#FFF6E5]",
  };
  return <span className={`inline-block px-2 py-0.5 border text-[10px] font-mono uppercase tracking-wider ${map[status] || "border-canvas-line"}`}>{status.replace("_", " ")}</span>;
}

function CreateComplaintDialog({ onClose, onCreated }) {
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState({
    customer_name: "", customer_phone: "", vehicle_details: "",
    source: "phone", category: "service", severity: "medium",
    description: "", assigned_to_user_id: "", branch_id: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        // Assignee picker uses the lightweight complaints-scoped endpoint so
        // Customer Care doesn't need the full `users.view` permission; the
        // branch picker uses `include_all=true` because intake registers
        // complaints ON BEHALF of every branch in the network.
        const [u, b] = await Promise.all([
          api.get("/complaints/assignees"),
          api.get("/branches", { params: { include_all: true, active_only: true } }),
        ]);
        setUsers(Array.isArray(u.data) ? u.data : (u.data.items || []));
        setBranches(b.data || []);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!(form.customer_name || "").trim() || !(form.customer_phone || "").trim() ||
        !form.assigned_to_user_id || !form.branch_id || !(form.description || "").trim()) {
      toast.error("Fill customer name, phone, description, assignee, and branch.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/complaints", form);
      toast.success(`Registered ${data.complaint_id}${data.ack_whatsapp_ok ? " · Ack sent" : ""}`);
      onCreated?.(data);
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="complaint-create-dialog">
      <form onSubmit={submit} className="bg-canvas-card border-2 border-ink w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-3 border-b border-canvas-line flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold">Register Complaint</h2>
          <button type="button" onClick={onClose} data-testid="complaint-create-close"><X size={20} /></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-4 text-sm">
          <label>
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">Customer Name</div>
            <input required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="complaint-customer-name" className="w-full px-2 py-1.5 bg-canvas border border-canvas-line" placeholder="Full name" />
          </label>
          <label>
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">Phone Number</div>
            <input required value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} data-testid="complaint-customer-phone" className="w-full px-2 py-1.5 bg-canvas border border-canvas-line font-mono" placeholder="10-digit mobile" />
          </label>
          <label className="col-span-2">
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">Vehicle Details</div>
            <input value={form.vehicle_details} onChange={(e) => setForm({ ...form, vehicle_details: e.target.value })} data-testid="complaint-vehicle-details" className="w-full px-2 py-1.5 bg-canvas border border-canvas-line font-mono" placeholder="Make / Model / Reg. No. — e.g. TVS Apache RTR 160 · AP16 AB 1234" />
          </label>
          <label>
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">Source</div>
            <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} data-testid="complaint-source" className="w-full px-2 py-1.5 bg-canvas border border-canvas-line">
              {SOURCES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
          </label>
          <label>
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">Category</div>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="complaint-category" className="w-full px-2 py-1.5 bg-canvas border border-canvas-line">
              {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
            </select>
          </label>
          <label>
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">Severity</div>
            <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} data-testid="complaint-severity" className="w-full px-2 py-1.5 bg-canvas border border-canvas-line">
              {SEVERITIES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
          </label>
          <label>
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">Branch</div>
            <select required value={form.branch_id} onChange={(e) => setForm({ ...form, branch_id: e.target.value })} data-testid="complaint-branch" className="w-full px-2 py-1.5 bg-canvas border border-canvas-line">
              <option value="">— Select —</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </label>
          <label className="col-span-2">
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">Assign To (Employee)</div>
            <select required value={form.assigned_to_user_id} onChange={(e) => setForm({ ...form, assigned_to_user_id: e.target.value })} data-testid="complaint-assignee" className="w-full px-2 py-1.5 bg-canvas border border-canvas-line">
              <option value="">— Select owner —</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}{u.branch_name ? ` · ${u.branch_name}` : ""}</option>)}
            </select>
          </label>
          <label className="col-span-2">
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">Description</div>
            <textarea required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="complaint-description" rows={3} className="w-full px-2 py-1.5 bg-canvas border border-canvas-line" placeholder="Complaint in customer's words…" />
          </label>
        </div>
        <div className="px-5 py-3 border-t border-canvas-line flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 border border-canvas-line text-xs font-mono uppercase tracking-wider">Cancel</button>
          <button type="submit" disabled={saving} data-testid="complaint-create-submit" className="px-4 py-1.5 bg-ink text-white text-xs font-semibold hover:bg-brand disabled:opacity-60">
            {saving ? "Registering…" : "Register & Send Ack"}
          </button>
        </div>
      </form>
    </div>
  );
}

function RowActions({ row, onChanged }) {
  const [busy, setBusy] = useState(false);
  const doStatus = async (status, extra = {}) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/complaints/${row.id}`, { status, ...extra });
      toast.success(`→ ${status}`);
      onChanged?.(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-1">
      {row.status === "open" && (
        <button onClick={() => doStatus("in_progress")} disabled={busy} data-testid={`complaint-start-${row.id}`} className="px-2 py-1 border border-canvas-line text-[10px] font-mono uppercase hover:border-ink" title="Mark In-Progress">Start</button>
      )}
      {row.status !== "closed" && row.status !== "resolved" && (
        <button
          onClick={() => {
            const t = window.prompt("Action taken (required — what was done to resolve?):");
            const trimmed = (t || "").trim();
            if (!trimmed) {
              toast.error("Resolution requires an action-taken note.");
              return;
            }
            doStatus("resolved", { action_taken: trimmed });
          }}
          disabled={busy}
          data-testid={`complaint-resolve-${row.id}`}
          className="px-2 py-1 border border-canvas-line text-[10px] font-mono uppercase hover:border-[#00B341] hover:text-[#00B341]"
          title="Mark Resolved"
        >
          Resolve
        </button>
      )}
      {row.status !== "closed" && (
        <button onClick={() => { const p = window.prompt("Closure proof (customer confirmation / WA message):"); if (p) doStatus("closed", { closure_proof: p }); }} disabled={busy} data-testid={`complaint-close-${row.id}`} className="px-2 py-1 border border-canvas-line text-[10px] font-mono uppercase hover:border-ink" title="Close with proof">Close</button>
      )}
      {row.status !== "closed" && row.escalation_level !== "L3" && (
        <button onClick={() => doStatus("escalated", { note: window.prompt("Escalation note?") || "" })} disabled={busy} data-testid={`complaint-escalate-${row.id}`} className="px-2 py-1 border border-canvas-line text-[10px] font-mono uppercase hover:border-destructive hover:text-destructive" title="Escalate to next level"><ArrowUp size={12} /></button>
      )}
    </div>
  );
}

export default function ComplaintsPage() {
  const { user } = useAuth();
  const canCreate = (user?.permissions || []).includes("complaints.create") || user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total: 0, by_status: {}, overdue: 0 });
  const [status, setStatus] = useState("");
  const [severity, setSeverity] = useState("");
  const [category, setCategory] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [q, setQ] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (severity) params.severity = severity;
      if (category) params.category = category;
      if (branchFilter) params.branch_id = branchFilter;
      if (q) params.q = q;
      const [{ data: list }, { data: sum }] = await Promise.all([
        api.get("/complaints", { params }),
        api.get("/complaints/summary", { params: branchFilter ? { branch_id: branchFilter } : {} }),
      ]);
      setRows(list || []);
      setSummary(sum);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [status, severity, category, branchFilter, q]);

  useEffect(() => { load(); }, [load]);

  const isOverdue = (r) => r.due_at && r.status !== "resolved" && r.status !== "closed" && new Date(r.due_at) < new Date();

  return (
    <div className="p-8 max-w-[1600px] mx-auto space-y-6">
      <PageHeader
        eyebrow="09 / Customer Care"
        title="Complaints"
        subtitle="Register, classify and drive every customer complaint to close — with SLA + WhatsApp acknowledgement."
        actions={
          canCreate && (
            <button onClick={() => setShowCreate(true)} data-testid="complaint-create-btn" className="px-4 py-2 bg-ink text-white text-xs font-mono uppercase tracking-wider hover:bg-brand flex items-center gap-2">
              <Plus size={14} /> New Complaint
            </button>
          )
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { l: "Total", v: summary.total, css: "border-canvas-line" },
          { l: "Open", v: summary.by_status.open || 0, css: "border-destructive bg-destructive/5" },
          { l: "In Progress", v: summary.by_status.in_progress || 0, css: "border-[#0057b7] bg-[#0057b7]/5" },
          { l: "Resolved", v: summary.by_status.resolved || 0, css: "border-[#00B341] bg-[#00B341]/5" },
          { l: "Overdue", v: summary.overdue, css: "border-[#E08600] bg-[#FFF6E5]" },
        ].map((t) => (
          <div key={t.l} className={`border-2 p-4 ${t.css}`} data-testid={`kpi-${t.l.toLowerCase().replace(" ", "-")}`}>
            <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">{t.l}</div>
            <div className="font-display text-3xl font-bold tabular-nums mt-1">{t.v}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap px-1" data-testid="complaint-filter-bar">
        <BranchFilter value={branchFilter} onChange={setBranchFilter} testId="complaints-branch-filter" kind="sales" />
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="filter-status" className="px-2 py-1 bg-canvas border border-canvas-line text-xs font-mono">
          <option value="">All Statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={severity} onChange={(e) => setSeverity(e.target.value)} data-testid="filter-severity" className="px-2 py-1 bg-canvas border border-canvas-line text-xs font-mono">
          <option value="">All Severities</option>
          {SEVERITIES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
        </select>
        <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="filter-category" className="px-2 py-1 bg-canvas border border-canvas-line text-xs font-mono">
          <option value="">All Categories</option>
          {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
        </select>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ID / customer / phone" data-testid="filter-q" className="px-2 py-1 bg-canvas border border-canvas-line text-xs font-mono flex-1 min-w-[220px]" />
      </div>

      <div className="border-2 border-ink">
        <table className="w-full text-sm">
          <thead className="bg-canvas-panel">
            <tr>
              {["ID", "Customer / Vehicle", "Category", "Severity", "Assignee", "Branch", "Due", "Status", "Actions"].map((h) => (
                <th key={h} className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-ink-muted">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="p-8 text-center text-ink-muted text-xs font-mono">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={9} className="p-8 text-center text-ink-muted text-xs">No complaints match the current filters.</td></tr>}
            {!loading && rows.map((r) => {
              const sev = SEVERITIES.find((s) => s.v === r.severity);
              const overdue = isOverdue(r);
              return (
                <Fragment key={r.id}>
                  <tr className="border-b border-canvas-line hover:bg-canvas-panel/50" data-testid={`complaint-row-${r.id}`}>
                    <td className="px-3 py-2 font-mono text-[11px] font-semibold">
                      <button
                        onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
                        data-testid={`complaint-toggle-${r.id}`}
                        className="hover:underline"
                        title="Show activity log"
                      >
                        {r.complaint_id}
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-semibold text-[13px]">{r.customer_snapshot?.name || "—"}</div>
                      <div className="font-mono text-[10px] text-ink-muted">{r.customer_snapshot?.phone || ""} · {r.vehicle_details || r.vehicle_snapshot?.vin || ""}</div>
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px] capitalize">{r.category}</td>
                    <td className="px-3 py-2"><span className={`px-2 py-0.5 border text-[10px] font-mono uppercase ${sev?.css || ""}`}>{r.severity}</span></td>
                    <td className="px-3 py-2 text-[12px]">{r.assigned_to_name}<div className="font-mono text-[10px] text-ink-muted">{r.escalation_level}</div></td>
                    <td className="px-3 py-2 font-mono text-[11px] text-ink-muted" data-testid={`complaint-branch-${r.id}`}>{r.branch_name || r.branch_id || "—"}</td>
                    <td className="px-3 py-2 font-mono text-[11px]">
                      {r.due_at ? (
                        <span className={overdue ? "text-destructive font-semibold" : "text-ink-muted"}>
                          {fmtDate(r.due_at)}
                          {overdue && <span className="ml-1"><Clock size={11} className="inline" /> OVERDUE</span>}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="px-3 py-2"><StatusPill status={r.status} /></td>
                    <td className="px-3 py-2"><RowActions row={r} onChanged={load} /></td>
                  </tr>
                  {expandedId === r.id && (
                    <tr className="bg-canvas-panel/40" data-testid={`complaint-actions-log-${r.id}`}>
                      <td colSpan={9} className="px-4 py-3">
                        <div className="text-[11px] font-mono uppercase tracking-wider text-ink-muted mb-2">Activity Log</div>
                        <div className="space-y-1.5">
                          {r.description && (
                            <div className="p-2 bg-white border border-canvas-line text-[11px]">
                              <div className="font-semibold text-[10px] text-ink-muted uppercase tracking-wider">Original complaint</div>
                              <div className="text-[12px] mt-0.5">{r.description}</div>
                            </div>
                          )}
                          {r.action_taken && (
                            <div className="p-2 bg-[#00B341]/5 border border-[#00B341]/30 text-[11px]">
                              <div className="font-semibold text-[10px] text-[#00713a] uppercase tracking-wider">Action taken</div>
                              <div className="text-[12px] mt-0.5">{r.action_taken}</div>
                            </div>
                          )}
                          {r.closure_proof && (
                            <div className="p-2 bg-canvas border border-canvas-line text-[11px]">
                              <div className="font-semibold text-[10px] text-ink-muted uppercase tracking-wider">Closure proof</div>
                              <div className="text-[12px] mt-0.5">{r.closure_proof}</div>
                            </div>
                          )}
                          {(r.actions || []).map((a, idx) => (
                            <div key={idx} className="flex items-start gap-2 text-[11px] font-mono">
                              <span className="text-ink-muted whitespace-nowrap">{fmtDate(a.at)}</span>
                              <span className="text-ink font-semibold uppercase text-[10px]">{a.kind}</span>
                              <span className="text-ink-muted">{a.by_name}</span>
                              {a.note && <span className="text-ink">— {a.note}</span>}
                              {a.from_status && <span className="text-ink-muted">({a.from_status} → {a.to_status})</span>}
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateComplaintDialog onClose={() => setShowCreate(false)} onCreated={load} />}
    </div>
  );
}
